import { formatDistanceStrict } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ExternalCharacterListItem } from '@cartwhl/common/api/getExternalCharacters';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Input from '@cartwhl/ui/atoms/Input';
import Loading from '@cartwhl/ui/atoms/Loading';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import CheckmarkIcon from '@cartwhl/ui/icons/CheckmarkIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import ExpandIcon from '@cartwhl/ui/icons/ExpandIcon';
import FileIcon from '@cartwhl/ui/icons/FileIcon';
import ListIcon from '@cartwhl/ui/icons/ListIcon';
import PenIcon from '@cartwhl/ui/icons/PenIcon';
import PersonIcon from '@cartwhl/ui/icons/PersonIcon';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';
import TrashIcon from '@cartwhl/ui/icons/TrashIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';
import AnimationPreview from '@cartwhl/ui/molecules/AnimationPreview';

type CartographerCharacterLibraryMode = 'compact' | 'panel' | 'modal';
type CartographerCharacterLibrarySortMode = 'date' | 'name';

interface CartographerCharacterLibraryToolbarConfig {
  showSearch?: boolean;
  searchQuery?: string;
  onSearchQueryChange?: (nextQuery: string) => void;
  searchPlaceholder?: string;
  showExpandButton?: boolean;
  onExpandClick?: () => void;
  expandTooltipText?: string;
  expandAriaLabel?: string;
  showEditToggle?: boolean;
  editMode?: boolean;
  onEditModeChange?: (isEditing: boolean) => void;
  editTooltipEnterText?: string;
  editTooltipExitText?: string;
}

interface CartographerCharacterLibraryProps {
  mode: CartographerCharacterLibraryMode;
  apiKey: string | null;
  characters: ExternalCharacterListItem[];
  charactersLoading: boolean;
  charactersError: string | null;
  loadingCharacterId: string | null;
  selectedCharacterId?: string | null;
  onSelectCharacter: (
    character: ExternalCharacterListItem,
  ) => void | Promise<unknown>;
  onDeleteCharacter?: (character: ExternalCharacterListItem) => void;
  onRenameCharacter?: (
    character: ExternalCharacterListItem,
    nextCharacterName: string,
  ) => Promise<boolean> | boolean;
  toolbar?: CartographerCharacterLibraryToolbarConfig;
  searchQuery?: string;
  searchEmptyMessage?: string;
  modalEditMode?: boolean;
  onModalEditModeChange?: (isEditing: boolean) => void;
  onViewAll?: () => void;
  className?: string;
  compactHeightPx?: number;
  compactColumns?: number;
  modalApiKeyMessage?: string;
  modalEmptyMessage?: string;
}

const DEFAULT_COMPACT_HEIGHT_PX = 284;
const DEFAULT_COMPACT_COLUMNS = 5;
const DEFAULT_MODAL_API_KEY_MESSAGE =
  'An active account is required to load your custom characters. Sign in and/or upgrade to use this feature.';
const DEFAULT_MODAL_EMPTY_MESSAGE =
  'No custom characters available. Create characters via the API or upload a model to get started.';
const DEFAULT_SEARCH_EMPTY_MESSAGE = 'No characters match your search.';
const DEFAULT_SEARCH_PLACEHOLDER = 'Search name or extension';
const DEFAULT_EXPAND_TOOLTIP = 'Open full character library';
const DEFAULT_EXPAND_ARIA_LABEL = 'Open full character library';
const DEFAULT_EDIT_TOOLTIP_ENTER = 'Enter edit mode';
const DEFAULT_EDIT_TOOLTIP_EXIT = 'Exit edit mode';
const DEFAULT_MODAL_SORT_MODE: CartographerCharacterLibrarySortMode = 'date';

function getCreatedAtSubtitle(createdAt: string): string | undefined {
  const parsedDate = new Date(createdAt);
  if (Number.isNaN(parsedDate.getTime())) return undefined;
  return formatDistanceStrict(parsedDate, new Date(), {
    addSuffix: true,
  });
}

function getExtensionLabel(extension?: string): string | null {
  if (!extension) return null;
  const normalized = extension.trim().toLowerCase().replace(/^\./, '');
  if (!normalized) return null;
  return `.${normalized}`;
}

function getCreatedAtTimestamp(createdAt: string): number | null {
  const parsedDate = new Date(createdAt);
  const timestamp = parsedDate.getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function compareCharacterNames(
  left: ExternalCharacterListItem,
  right: ExternalCharacterListItem,
): number {
  return (left.characterName ?? '').localeCompare(
    right.characterName ?? '',
    undefined,
    {
      sensitivity: 'base',
      numeric: true,
    },
  );
}

function isSubsequenceMatch(needle: string, haystack: string): boolean {
  if (!needle) return true;
  let needleIdx = 0;
  for (const char of haystack) {
    if (char === needle[needleIdx]) {
      needleIdx += 1;
      if (needleIdx === needle.length) return true;
    }
  }
  return false;
}

function matchesCharacterSearch(
  character: ExternalCharacterListItem,
  searchQuery: string,
): boolean {
  const normalizedQuery = searchQuery.toLowerCase().trim();
  if (!normalizedQuery) return true;

  const normalizedExtension = (character.extension ?? '')
    .toLowerCase()
    .replace(/^\./, '');
  const dotExtension = normalizedExtension ? `.${normalizedExtension}` : '';
  const normalizedName = (character.characterName ?? '').toLowerCase();
  const searchableTokens = [
    normalizedName,
    normalizedExtension,
    dotExtension,
    normalizedExtension ? `${normalizedName}${dotExtension}` : normalizedName,
  ];

  if (searchableTokens.some((token) => token.includes(normalizedQuery))) {
    return true;
  }

  const compactQuery = normalizedQuery.replace(/[\s._-]+/g, '');
  if (!compactQuery) return true;

  return searchableTokens.some((token) =>
    isSubsequenceMatch(compactQuery, token.replace(/[\s._-]+/g, '')),
  );
}

export default function CartographerCharacterLibrary({
  mode,
  apiKey,
  characters,
  charactersLoading,
  charactersError,
  loadingCharacterId,
  selectedCharacterId,
  onSelectCharacter,
  onDeleteCharacter,
  onRenameCharacter,
  toolbar,
  searchQuery,
  searchEmptyMessage = DEFAULT_SEARCH_EMPTY_MESSAGE,
  modalEditMode,
  onModalEditModeChange,
  onViewAll,
  className = '',
  compactHeightPx = DEFAULT_COMPACT_HEIGHT_PX,
  compactColumns = DEFAULT_COMPACT_COLUMNS,
  modalApiKeyMessage = DEFAULT_MODAL_API_KEY_MESSAGE,
  modalEmptyMessage = DEFAULT_MODAL_EMPTY_MESSAGE,
}: CartographerCharacterLibraryProps) {
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [modalSortMode, setModalSortMode] =
    useState<CartographerCharacterLibrarySortMode>(DEFAULT_MODAL_SORT_MODE);
  const resolvedSearchQuery =
    toolbar?.searchQuery ?? searchQuery ?? internalSearchQuery;
  const normalizedSearchQuery = resolvedSearchQuery.trim();
  const hasSearchQuery = normalizedSearchQuery.length > 0;
  const sortedCharacters = useMemo(() => {
    if (mode !== 'modal') {
      return characters;
    }
    const nextCharacters = [...characters];
    nextCharacters.sort((left, right) => {
      if (modalSortMode === 'name') {
        return compareCharacterNames(left, right);
      }
      const leftTimestamp = getCreatedAtTimestamp(left.createdAt) ?? -Infinity;
      const rightTimestamp =
        getCreatedAtTimestamp(right.createdAt) ?? -Infinity;
      if (leftTimestamp !== rightTimestamp) {
        return rightTimestamp - leftTimestamp;
      }
      return compareCharacterNames(left, right);
    });
    return nextCharacters;
  }, [characters, mode, modalSortMode]);
  const visibleCharacters = useMemo(
    () =>
      hasSearchQuery
        ? sortedCharacters.filter((character) =>
            matchesCharacterSearch(character, normalizedSearchQuery),
          )
        : sortedCharacters,
    [hasSearchQuery, normalizedSearchQuery, sortedCharacters],
  );
  const hasApiKey = Boolean(apiKey);
  const hasCharacters = characters.length > 0;
  const hasVisibleCharacters = visibleCharacters.length > 0;
  const hasError = Boolean(charactersError);
  const selectionInProgress = loadingCharacterId !== null;
  const canRenderGrid = hasApiKey && !charactersLoading && hasVisibleCharacters;
  const showSearchEmptyState =
    hasSearchQuery &&
    hasCharacters &&
    !hasVisibleCharacters &&
    hasApiKey &&
    !charactersLoading &&
    !hasError;
  const canManageCharacters =
    Boolean(onDeleteCharacter) || Boolean(onRenameCharacter);
  const showSearchControl = toolbar?.showSearch ?? false;
  const showExpandControl = Boolean(
    toolbar?.showExpandButton && toolbar.onExpandClick,
  );
  const showEditToggleControl = Boolean(
    mode === 'modal' &&
      canManageCharacters &&
      (toolbar?.showEditToggle ?? false),
  );
  const showSortControl = mode === 'modal';
  const hasToolbarControls =
    showSearchControl ||
    showExpandControl ||
    showEditToggleControl ||
    showSortControl;
  const searchPlaceholder =
    toolbar?.searchPlaceholder ?? DEFAULT_SEARCH_PLACEHOLDER;
  const [internalModalEditMode, setInternalModalEditMode] = useState(false);
  const resolvedToolbarEditMode = toolbar?.editMode;
  const resolvedToolbarOnEditModeChange = toolbar?.onEditModeChange;
  const [editingCharacterId, setEditingCharacterId] = useState<string | null>(
    null,
  );
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSavingId, setRenameSavingId] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement | null>(null);
  const isModalEditMode =
    mode === 'modal' && canManageCharacters
      ? (resolvedToolbarEditMode ?? modalEditMode ?? internalModalEditMode)
      : false;

  const setSearchQueryValue = useCallback(
    (nextQuery: string) => {
      if (toolbar?.onSearchQueryChange) {
        toolbar.onSearchQueryChange(nextQuery);
        return;
      }
      if (toolbar?.searchQuery !== undefined || searchQuery !== undefined) {
        return;
      }
      setInternalSearchQuery(nextQuery);
    },
    [searchQuery, toolbar],
  );

  const setIsModalEditMode = useCallback(
    (next: boolean) => {
      if (resolvedToolbarOnEditModeChange) {
        resolvedToolbarOnEditModeChange(next);
        return;
      }
      if (onModalEditModeChange) {
        onModalEditModeChange(next);
        return;
      }
      setInternalModalEditMode(next);
    },
    [onModalEditModeChange, resolvedToolbarOnEditModeChange],
  );

  const cancelRename = useCallback(() => {
    setEditingCharacterId(null);
    setRenameDraft('');
  }, []);

  const editingCharacter = useMemo(
    () =>
      visibleCharacters.find(
        (character) => character.jobID === editingCharacterId,
      ) ?? null,
    [editingCharacterId, visibleCharacters],
  );

  const commitRename = useCallback(async () => {
    if (!editingCharacter || !onRenameCharacter) return;

    const nextName = renameDraft.trim();
    if (!nextName) return;

    if (nextName === editingCharacter.characterName) {
      cancelRename();
      return;
    }

    setRenameSavingId(editingCharacter.jobID);
    const ok = await onRenameCharacter(editingCharacter, nextName);
    setRenameSavingId(null);

    if (ok) {
      cancelRename();
    }
  }, [cancelRename, editingCharacter, onRenameCharacter, renameDraft]);

  useEffect(() => {
    if (mode !== 'modal' || !canManageCharacters) {
      if (isModalEditMode) {
        setIsModalEditMode(false);
      }
      cancelRename();
      return;
    }
    if (editingCharacterId && !editingCharacter) {
      cancelRename();
    }
  }, [
    canManageCharacters,
    cancelRename,
    editingCharacter,
    editingCharacterId,
    isModalEditMode,
    mode,
    setIsModalEditMode,
  ]);

  useEffect(() => {
    if (!isModalEditMode && editingCharacterId) {
      cancelRename();
    }
  }, [cancelRename, editingCharacterId, isModalEditMode]);

  useEffect(() => {
    if (!editingCharacterId || !renameInputRef.current) return;
    const input = renameInputRef.current;
    input.focus();
    input.setSelectionRange(0, input.value.length);
  }, [editingCharacterId]);

  const toolbarNode = hasToolbarControls ? (
    <div
      className={`flex shrink-0 items-center gap-2 ${
        mode === 'modal' ? 'bg-primary sticky top-0 z-[3] pb-1' : 'px-2 pt-2'
      }`}
    >
      {showSearchControl && (
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="text-gray-45 pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
          <Input
            name={`${mode}-character-search`}
            placeholder={searchPlaceholder}
            value={resolvedSearchQuery}
            onChange={(event) => setSearchQueryValue(event.target.value)}
            autoComplete="off"
            className="h-8 pr-8 pl-8"
          />
          {resolvedSearchQuery && (
            <button
              type="button"
              aria-label="Clear character search"
              className="text-gray-45 hover:text-gray-90 absolute top-1/2 right-2 -translate-y-1/2 transition-colors dark:hover:text-white"
              onClick={() => setSearchQueryValue('')}
            >
              <CrossIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {showExpandControl && (
        <Tooltip
          text={toolbar?.expandTooltipText ?? DEFAULT_EXPAND_TOOLTIP}
          side="top"
          inModal={mode === 'modal'}
        >
          <Button
            size="sm"
            variant="secondary"
            className="h-8 shrink-0"
            icon={<ExpandIcon />}
            onClick={() => toolbar?.onExpandClick?.()}
            aria-label={toolbar?.expandAriaLabel ?? DEFAULT_EXPAND_ARIA_LABEL}
          />
        </Tooltip>
      )}

      {showSortControl && (
        <Dropdown
          align="end"
          side="bottom"
          inModal={mode === 'modal'}
          trigger={
            <button
              type="button"
              className="bg-gray-15/25 text-gray-90 dark:bg-gray-90/25 dark:text-gray-15 hover:bg-gray-15 dark:hover:bg-gray-90 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors"
              aria-label={`Order characters (${modalSortMode === 'date' ? 'By Date' : 'By Name'})`}
            >
              <ListIcon className="h-4 w-4" />
            </button>
          }
        >
          <Dropdown.Item
            icon={
              <CheckmarkIcon
                className={`h-4 w-4 ${modalSortMode === 'date' ? '' : 'opacity-0'}`}
              />
            }
            variant={modalSortMode === 'date' ? 'blue' : 'default'}
            onClick={() => setModalSortMode('date')}
          >
            By Date
          </Dropdown.Item>
          <Dropdown.Item
            icon={
              <CheckmarkIcon
                className={`h-4 w-4 ${modalSortMode === 'name' ? '' : 'opacity-0'}`}
              />
            }
            variant={modalSortMode === 'name' ? 'blue' : 'default'}
            onClick={() => setModalSortMode('name')}
          >
            By Name
          </Dropdown.Item>
        </Dropdown>
      )}

      {showEditToggleControl && (
        <Tooltip
          text={
            isModalEditMode
              ? (toolbar?.editTooltipExitText ?? DEFAULT_EDIT_TOOLTIP_EXIT)
              : (toolbar?.editTooltipEnterText ?? DEFAULT_EDIT_TOOLTIP_ENTER)
          }
          side="top"
          inModal
        >
          <Button
            variant={isModalEditMode ? 'primary' : 'secondary'}
            size="sm"
            className="h-8 shrink-0 px-4"
            onClick={() => setIsModalEditMode(!isModalEditMode)}
          >
            {isModalEditMode ? 'Done' : 'Edit'}
          </Button>
        </Tooltip>
      )}
    </div>
  ) : null;

  const prioritizedThumbnailCount =
    mode === 'compact'
      ? Math.max(1, compactColumns * 2)
      : mode === 'panel'
        ? 4
        : 8;

  const tiles = visibleCharacters.map((character, characterIndex) => {
    const isLoadingThis = loadingCharacterId === character.jobID;
    const isEditingThis = editingCharacterId === character.jobID;
    const isPrioritizedThumbnail = characterIndex < prioritizedThumbnailCount;
    const canSelect =
      Boolean(character.characterFileURL) &&
      !selectionInProgress &&
      !(mode === 'modal' && isModalEditMode);
    const timeSubtitle =
      mode === 'compact'
        ? undefined
        : getCreatedAtSubtitle(character.createdAt);
    const extensionLabel =
      mode === 'modal' ? getExtensionLabel(character.extension) : null;

    const preview = (
      <AnimationPreview
        className={!canSelect && !isLoadingThis ? 'opacity-50' : ''}
        active={selectedCharacterId === character.jobID}
        onClick={
          canSelect
            ? async () => {
                await onSelectCharacter(character);
              }
            : undefined
        }
        title={
          mode === 'compact' || mode === 'modal'
            ? undefined
            : character.characterName
        }
        subtitle={mode === 'modal' ? undefined : timeSubtitle}
        mediaPath={character.thumbnailURL ?? ''}
        mediaType="image"
        aspect={mode === 'compact' ? 'square' : 'animation'}
        loading={isLoadingThis}
        imageLoading={isPrioritizedThumbnail ? 'eager' : 'lazy'}
        imageDecoding="async"
        imageFetchPriority={isPrioritizedThumbnail ? 'high' : 'low'}
      />
    );

    if (mode === 'modal') {
      const showActions = isModalEditMode && !isEditingThis;
      const showDeleteAction = showActions && Boolean(onDeleteCharacter);
      const showRenameAction = showActions && Boolean(onRenameCharacter);
      const renameLoading = renameSavingId === character.jobID;

      return (
        <div key={character.jobID} className="flex flex-col">
          {preview}
          <div className="mt-2 flex w-full items-center gap-2">
            {isEditingThis ? (
              <input
                ref={renameInputRef}
                type="text"
                value={renameDraft}
                disabled={renameLoading}
                onChange={(event) => setRenameDraft(event.target.value)}
                onBlur={() => {
                  if (!renameLoading) {
                    cancelRename();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void commitRename();
                  }
                  if (event.key === 'Escape') {
                    cancelRename();
                  }
                }}
                className="text-primary h-7 min-w-0 flex-1 border-none bg-transparent px-0 outline-hidden"
                aria-label={`Rename ${character.characterName}`}
              />
            ) : (
              <Text
                as="span"
                variant="studio/body/sm"
                className="text-primary min-w-0 flex-1 truncate"
              >
                {character.characterName}
              </Text>
            )}
            {showRenameAction && (
              <Button
                variant="tertiary"
                size="sm"
                className="h-7"
                icon={<PenIcon />}
                aria-label={`Rename ${character.characterName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setEditingCharacterId(character.jobID);
                  setRenameDraft(character.characterName);
                }}
              />
            )}
            {showDeleteAction && (
              <Button
                variant="secondary-destructive"
                size="sm"
                className="h-7"
                icon={<TrashIcon />}
                aria-label={`Delete ${character.characterName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteCharacter?.(character);
                }}
              />
            )}
          </div>
          <div className="mt-1 flex w-full items-center gap-2">
            {timeSubtitle && (
              <Text
                as="span"
                variant="studio/body/sm"
                className="text-gray-45 min-w-0 flex-1 truncate"
              >
                {timeSubtitle}
              </Text>
            )}
            {extensionLabel && (
              <span className="border-primary bg-secondary ml-auto inline-flex shrink-0 rounded-full border px-2 py-0.5">
                <Text
                  as="span"
                  variant="studio/body/medium/xs"
                  className="text-secondary"
                >
                  {extensionLabel}
                </Text>
              </span>
            )}
          </div>
        </div>
      );
    }

    return <div key={character.jobID}>{preview}</div>;
  });

  const compactFooter = () => {
    if (mode !== 'compact' || !onViewAll || charactersLoading) return null;

    if (!hasApiKey) return null;

    if (hasError && !hasCharacters) {
      return (
        <div className="border-gray-30 dark:border-gray-60 shrink-0 border-t px-2 py-1.5">
          <GoButton className="h-9 w-full max-w-full py-0" onClick={onViewAll}>
            <span className="inline-flex items-center justify-center gap-2">
              <PersonIcon className="shrink-0" />
              Open library
            </span>
          </GoButton>
        </div>
      );
    }

    if (!hasError && !hasCharacters) return null;

    return (
      <div className="border-gray-30 dark:border-gray-60 flex shrink-0 items-center border-t px-2 py-1.5">
        <Text
          variant="studio/body/medium/xs"
          className="text-tertiary min-w-0 truncate"
        >
          {characters.length} saved
        </Text>
      </div>
    );
  };

  if (mode === 'modal') {
    return (
      <div className={`flex flex-col gap-4 ${className}`}>
        {toolbarNode}
        {!hasApiKey && (
          <Text variant="studio/body/md" className="text-secondary">
            {modalApiKeyMessage}
          </Text>
        )}
        {hasApiKey && charactersLoading && (
          <div className="flex flex-col items-center justify-center gap-2 py-8">
            <Loading className="text-gray-45 h-8 w-8" />
            <Text variant="studio/body/sm" className="text-tertiary">
              Loading characters…
            </Text>
          </div>
        )}
        {hasApiKey && !charactersLoading && hasError && !hasCharacters && (
          <Text variant="studio/body/sm" className="text-red">
            {charactersError}
          </Text>
        )}
        {showSearchEmptyState && (
          <Text
            variant="studio/body/md"
            className="text-secondary py-6 text-center"
          >
            {searchEmptyMessage}
          </Text>
        )}
        {hasApiKey && !charactersLoading && !hasError && !hasCharacters && (
          <Text
            variant="studio/body/md"
            className="text-secondary py-6 text-center"
          >
            {modalEmptyMessage}
          </Text>
        )}
        {canRenderGrid && (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {tiles}
          </div>
        )}
      </div>
    );
  }

  const panelContent = (
    <div className="relative isolate flex h-full min-h-0 flex-1 flex-col">
      {toolbarNode}
      <div className="relative min-h-0 flex-1">
        {canRenderGrid && (
          <div
            className={`styled-scrollbar h-full w-full overflow-auto ${
              mode === 'compact' ? 'p-2' : 'p-3'
            }`}
          >
            {mode === 'compact' ? (
              <div
                className="grid gap-1"
                style={{
                  gridTemplateColumns: `repeat(${compactColumns}, minmax(0, 1fr))`,
                }}
              >
                {tiles}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">{tiles}</div>
            )}
          </div>
        )}

        {!hasApiKey && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
            <PersonIcon className="text-gray-45 h-8 w-8 shrink-0" />
            <Text
              variant="studio/body/medium/sm"
              className={`text-secondary ${mode === 'compact' ? 'max-w-[240px]' : ''}`}
            >
              Sign in or upgrade your account to load your saved characters.
            </Text>
          </div>
        )}

        {hasApiKey && charactersLoading && (
          <div
            className={`absolute inset-0 z-[2] flex flex-col items-center justify-center gap-2 ${
              mode === 'compact'
                ? 'bg-gray-15/95 dark:bg-gray-85/95'
                : 'bg-secondary/95'
            }`}
          >
            <Loading className="text-gray-45 h-8 w-8" />
            <Text variant="studio/body/medium/sm" className="text-tertiary">
              Loading library…
            </Text>
          </div>
        )}

        {hasApiKey && !charactersLoading && hasError && !hasCharacters && (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
            <Text variant="studio/body/medium/sm" className="text-red">
              {charactersError}
            </Text>
          </div>
        )}

        {showSearchEmptyState && (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
            <Text variant="studio/body/medium/sm" className="text-secondary">
              {searchEmptyMessage}
            </Text>
          </div>
        )}

        {hasApiKey && !charactersLoading && !hasError && !hasCharacters && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
            <FileIcon
              className={`text-gray-45 shrink-0 ${mode === 'compact' ? 'h-8 w-8' : 'h-7 w-7'}`}
            />
            <Text
              variant="studio/body/medium/sm"
              className={`text-secondary ${mode === 'compact' ? 'max-w-[260px]' : ''}`}
            >
              {mode === 'compact'
                ? 'No saved characters yet. Load your first character to get started!'
                : 'No saved characters yet.'}
            </Text>
          </div>
        )}
      </div>
    </div>
  );

  if (mode === 'compact') {
    return (
      <div
        className={`border-gray-30 bg-gray-15 dark:border-gray-60 dark:bg-gray-85 flex flex-col overflow-hidden rounded-lg border ${className}`}
        style={{ height: compactHeightPx }}
      >
        {panelContent}
        {compactFooter()}
      </div>
    );
  }

  return (
    <div
      className={`border-secondary bg-secondary relative min-h-0 flex-1 overflow-hidden rounded-xl border ${className}`}
    >
      {panelContent}
    </div>
  );
}

export type {
  CartographerCharacterLibraryMode,
  CartographerCharacterLibraryToolbarConfig,
};
